import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { BRAND as FALLBACK } from "@/lib/brand";
import { setDocumentBrand } from "@/lib/documentTemplate";

// Single-flight cache so every screen shares the same company payload.
let _cache = null;
let _promise = null;
const _listeners = new Set();

async function _fetch() {
  if (_promise) return _promise;
  _promise = (async () => {
    try {
      const { data } = await api.get("/company");
      _cache = data;
      setDocumentBrand(data);
    } catch {
      _cache = { ...FALLBACK };
    } finally {
      _promise = null;
      _listeners.forEach((cb) => cb(_cache));
    }
    return _cache;
  })();
  return _promise;
}

/**
 * Hook: returns the (dynamic) company profile. Falls back to the static
 * BRAND config if the network is unavailable so the app never renders
 * a broken logo/name.
 */
export function useCompany() {
  const [company, setCompany] = useState(_cache || FALLBACK);

  useEffect(() => {
    const cb = (c) => setCompany({ ...FALLBACK, ...c });
    _listeners.add(cb);
    if (!_cache) _fetch().then((c) => setCompany({ ...FALLBACK, ...c }));
    return () => { _listeners.delete(cb); };
  }, []);

  const reload = useCallback(async () => {
    _cache = null;
    const c = await _fetch();
    return { ...FALLBACK, ...c };
  }, []);

  return { company, reload };
}

export function refreshCompany() {
  _cache = null;
  return _fetch();
}
